import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon, TrashIcon, BuildingLibraryIcon,
  CreditCardIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { payrollEngineApi } from '../../../services/api';
import { Spinner, Modal, EmptyState, ConfirmModal } from '../../../components/shared/index';

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings',  label: 'Savings' },
];

const ALLOCATION_TYPES = [
  { value: 'remainder', label: 'Remainder (catches all leftover)' },
  { value: 'percent',   label: 'Percent of net' },
  { value: 'fixed',     label: 'Fixed amount' },
];

const EMPTY = {
  bank_name: '',
  nickname: '',
  account_type: 'checking',
  routing_number: '',
  account_number: '',
  allocation_type: 'remainder',
  allocation_value: '',
  priority: 1,
};

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);
}

function AllocationLabel({ acc }) {
  if (acc.allocation_type === 'remainder') {
    return <span className="text-xs font-semibold text-green-700">Remainder</span>;
  }
  if (acc.allocation_type === 'percent') {
    return <span className="text-xs font-semibold text-blue-700">{Number(acc.allocation_value).toFixed(1)}% of net</span>;
  }
  return <span className="text-xs font-semibold text-purple-700">{fmtMoney(acc.allocation_value)} fixed</span>;
}

function BankAccountForm({ onSubmit, onClose, pending }) {
  const [form, setForm] = useState(EMPTY);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const routingValid = /^\d{9}$/.test(String(form.routing_number));
  const accountValid = /^\d{4,17}$/.test(String(form.account_number));
  const needsValue   = form.allocation_type !== 'remainder';
  const valueValid   = !needsValue || (form.allocation_value !== '' && Number(form.allocation_value) > 0);

  const valid = form.bank_name.trim() && routingValid && accountValid && valueValid;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      bank_name: form.bank_name.trim(),
      nickname: form.nickname.trim() || null,
      account_type: form.account_type,
      routing_number: form.routing_number,
      account_number: form.account_number,
      allocation_type: form.allocation_type,
      allocation_value: needsValue ? Number(form.allocation_value) : null,
      priority: Number(form.priority) || 1,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          Only <strong>one</strong> account per employee can be marked <code>remainder</code>.
          Adding a new remainder will archive the previous one.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Bank name *</label>
          <input
            required
            className="input"
            value={form.bank_name}
            onChange={(e) => set('bank_name', e.target.value)}
            placeholder="Chase, Wells Fargo, …"
          />
        </div>

        <div>
          <label className="label">Nickname</label>
          <input
            className="input"
            value={form.nickname}
            onChange={(e) => set('nickname', e.target.value)}
            placeholder="e.g. Primary"
          />
        </div>

        <div>
          <label className="label">Account type *</label>
          <select
            className="input"
            value={form.account_type}
            onChange={(e) => set('account_type', e.target.value)}
          >
            {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div>
          <label className="label">Priority</label>
          <input
            type="number" min="1" step="1"
            className="input"
            value={form.priority}
            onChange={(e) => set('priority', e.target.value)}
          />
        </div>

        <div>
          <label className="label">Routing number * (9 digits)</label>
          <input
            required
            inputMode="numeric"
            maxLength={9}
            className={clsx('input', form.routing_number && !routingValid && 'border-red-400')}
            value={form.routing_number}
            onChange={(e) => set('routing_number', e.target.value.replace(/\D/g, ''))}
          />
          {form.routing_number && !routingValid && (
            <p className="text-xs text-red-600 mt-1">Must be exactly 9 digits.</p>
          )}
        </div>

        <div>
          <label className="label">Account number * (4–17 digits)</label>
          <div className="relative">
            <input
              required
              type={showAccountNumber ? 'text' : 'password'}
              inputMode="numeric"
              maxLength={17}
              className={clsx('input pr-16', form.account_number && !accountValid && 'border-red-400')}
              value={form.account_number}
              onChange={(e) => set('account_number', e.target.value.replace(/\D/g, ''))}
            />
            <button
              type="button"
              onClick={() => setShowAccountNumber(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-600 hover:underline"
            >
              {showAccountNumber ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div>
          <label className="label">Allocation type *</label>
          <select
            className="input"
            value={form.allocation_type}
            onChange={(e) => set('allocation_type', e.target.value)}
          >
            {ALLOCATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {needsValue && (
          <div>
            <label className="label">
              Allocation value * {form.allocation_type === 'percent' ? '(%)' : '($)'}
            </label>
            <input
              required
              type="number" min="0.01" step="0.01"
              className="input"
              value={form.allocation_value}
              onChange={(e) => set('allocation_value', e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button
          type="submit"
          disabled={!valid || pending}
          className={clsx('btn-primary', (!valid || pending) && 'opacity-50')}
        >
          {pending ? 'Saving…' : 'Add account'}
        </button>
      </div>
    </form>
  );
}

export default function BankAccountsList({ employeeId, canEdit = true }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['bank-accounts', employeeId],
    queryFn:  () => payrollEngineApi.listBankAccounts(employeeId).then(r => r.data || []),
    enabled:  !!employeeId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['bank-accounts', employeeId] });

  const add = useMutation({
    mutationFn: (data) => payrollEngineApi.addBankAccount(employeeId, data),
    onSuccess: () => { toast.success('Bank account added'); setAddOpen(false); invalidate(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to add bank account'),
  });

  const remove = useMutation({
    mutationFn: (id) => payrollEngineApi.deleteBankAccount(id),
    onSuccess: () => { toast.success('Bank account removed'); invalidate(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to remove bank account'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="section-title">Direct deposit accounts</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Net pay is split across these accounts by priority and allocation type.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setAddOpen(true)} className="btn-primary">
            <PlusIcon className="w-4 h-4" /> Add bank account
          </button>
        )}
      </div>

      {accounts.length === 0 ? (
        <EmptyState
          title="No bank accounts"
          description="Add at least one account with an allocation of 'remainder' to receive direct deposit."
          action={canEdit ? (
            <button onClick={() => setAddOpen(true)} className="btn-primary">
              <PlusIcon className="w-4 h-4" /> Add bank account
            </button>
          ) : null}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map((acc) => (
            <div key={acc.id} className="card p-4 flex items-start gap-3">
              <div className={clsx(
                'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                acc.account_type === 'savings'
                  ? 'bg-green-50 text-green-600'
                  : 'bg-blue-50 text-blue-600',
              )}>
                {acc.account_type === 'savings'
                  ? <BuildingLibraryIcon className="w-5 h-5" />
                  : <CreditCardIcon className="w-5 h-5" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 truncate">
                    {acc.nickname || acc.bank_name}
                  </span>
                  <span className="text-xs text-gray-500 capitalize">{acc.account_type}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{acc.bank_name}</div>
                <div className="text-sm font-mono text-gray-700 mt-1">****{acc.last_four}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <AllocationLabel acc={acc} />
                  <span className="text-xs text-gray-400">Priority {acc.priority || 1}</span>
                </div>
              </div>

              {canEdit && (
                <button
                  onClick={() => setConfirm(acc)}
                  className="p-1.5 rounded hover:bg-red-50 text-red-600 shrink-0"
                  title="Remove"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add bank account" size="lg">
        <BankAccountForm
          onSubmit={(data) => add.mutate(data)}
          onClose={() => setAddOpen(false)}
          pending={add.isPending}
        />
      </Modal>

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove.mutate(confirm.id)}
        title="Remove this bank account?"
        message={`${confirm?.bank_name} ****${confirm?.last_four} will no longer receive direct deposits. This cannot be undone.`}
        danger
      />
    </div>
  );
}
